import Button from "@lhd/components/Button";
import { APP_TYPES } from "@lhd/components/CloneApp/constants";
import {
  useChangeStatusV2,
  useFetchOneAppByTypeV2,
  useFetchRecordV2,
} from "@lhd/hooks";
import { AppRecordV2, CONTRACT_STATUS, IPermissionItem } from "@lhd/types";
import { Tooltip } from "antd";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";

const NAME_APP_PART_WORKER_REPRESENTATIVE = "使用者職氏名";
const NAME_APP_PART_WORKER = "社員選択";
export interface ReceivedProps {
  configShowBtnsOnHeader?: {
    btnSave?: boolean;
    saveAsNew?: boolean;
    btnShare?: boolean;
    btnDuplicate?: boolean;
    btnToCreate?: boolean;
    btnPrintPdf?: boolean;
  };

  worker?: boolean;
  permission?: IPermissionItem;
}

const MODAL_CONTENT = {
  [CONTRACT_STATUS.PENDING_SIGNATURE]:
    "署名依頼後は、内容の編集ができなくなります。\nよろしいでしょうか。",
  [CONTRACT_STATUS.DRAFTING]:
    "契約を取り下げ、内容を編集します。\nよろしいでしょうか。",
  [CONTRACT_STATUS.ARCHIVED]: "本契約は無効となります。よろしいでしょうか。",
  [CONTRACT_STATUS.ACTIVE]: "本契約は無効となります。よろしいでしょうか。",
  ARCHIVED_TO_DRAFTING:
    "社員側では、本契約が表示されなくなります。\nよろしいでしょうか。",
};
const useEdit = (props: ReceivedProps) => {
  const { appId = "", id = "" } = useParams();
  const appType = APP_TYPES.EMPLOYMENT_CONTRACTS;
  const [openModalChangeStatus, setOpenModalChangeStatus] = useState(false);
  const [targetStatus, setTargetStatus] = useState<CONTRACT_STATUS | null>(
    null,
  );

  const [modalContent, setModalContent] = useState<string | null>(
    MODAL_CONTENT.ARCHIVED_TO_DRAFTING,
  );

  const openConfirmModal = (status: CONTRACT_STATUS) => {
    if (
      currentContractStatus === CONTRACT_STATUS.ARCHIVED &&
      status === CONTRACT_STATUS.DRAFTING
    ) {
      setModalContent(MODAL_CONTENT.ARCHIVED_TO_DRAFTING);
    } else {
      setModalContent(
        MODAL_CONTENT[status as unknown as keyof typeof MODAL_CONTENT],
      );
    }
    setTargetStatus(status);
    setOpenModalChangeStatus(true);
  };
  const { companyId: companyIdParam } = useParams();
  const {
    data: recordDataRaw,
    isLoading: isLoadingRecord,
    isSuccess: isSuccessRecord,
  } = useFetchRecordV2({
    appType,
    appId,
    recordId: id,
    companyId: companyIdParam,
  });

  const {
    data: dataApp,
    isLoading: isLoadingApp,
    isSuccess: isSuccessApp,
  } = useFetchOneAppByTypeV2(appType, companyIdParam);
  const [searchParams] = useSearchParams();
  const pageMode = searchParams.get("pageMode");
  const isEditMode = pageMode === "edit";

  const currentContractStatus =
    recordDataRaw?.data.contract_info?.contract_status;

  const [workerSigner, companyRepresentative] = useMemo(() => {
    if (!recordDataRaw || !dataApp) return [null, null];

    const workerSignerPart = dataApp?.app_parts?.find(
      (x) => x.name === NAME_APP_PART_WORKER_REPRESENTATIVE,
    );
    const companyRepresentativePart = dataApp?.app_parts?.find(
      (x) => x.name === NAME_APP_PART_WORKER,
    );

    const workerSigner = recordDataRaw?.data.regular_values.find(
      (x) => x.app_part_id === Number(workerSignerPart?.id),
    )?.value;
    const companyRepresentative = recordDataRaw?.data.regular_values.find(
      (x) => x.app_part_id === Number(companyRepresentativePart?.id),
    )?.value;

    return [workerSigner, companyRepresentative];
  }, [recordDataRaw?.data, dataApp?.app_parts]);

  const { mutateAsync: changeStatusAsync, isLoading: isLoadingChangeStatus } =
    useChangeStatusV2({
      appType,
      appId,
      companyId: companyIdParam,
      recordId: id,
    });

  const activeBtns = useMemo(() => {
    if (!workerSigner?.length || !companyRepresentative?.length) {
      return {
        archive: false,
        withdraw: false,
        requestSignature: false,
      };
    }

    switch (currentContractStatus) {
      case CONTRACT_STATUS.DRAFTING:
        return {
          archive: false,
          withdraw: false,
          requestSignature: true,
        };
      case CONTRACT_STATUS.PENDING_SIGNATURE:
        return {
          archive: false,
          withdraw: true,
          requestSignature: false,
        };
      case CONTRACT_STATUS.ACTIVE:
        return {
          archive: true,
          withdraw: false,
          requestSignature: false,
        };
      default:
        return {
          archive: false,
          withdraw: true,
          requestSignature: false,
        };
    }
  }, [workerSigner, companyRepresentative, currentContractStatus]);

  const extendsButtonRight = (record?: AppRecordV2, hasChanged?: boolean) => {
    if (props.worker) {
      return <></>;
    }
    return (
      <>
        <Button
          disabled={!activeBtns.archive || !isEditMode}
          label="過去ログ"
          onClick={() => openConfirmModal(CONTRACT_STATUS.ARCHIVED)}
        />
        <Button
          disabled={!activeBtns.withdraw || !isEditMode}
          label={
            currentContractStatus === CONTRACT_STATUS.ARCHIVED
              ? "戻す"
              : "取り下げ"
          }
          onClick={() => openConfirmModal(CONTRACT_STATUS.DRAFTING)}
        />

        <Tooltip
          title="契約当事者をすべて選択してください"
          trigger="hover"
          open={
            !activeBtns.requestSignature &&
            currentContractStatus === CONTRACT_STATUS.DRAFTING
              ? undefined
              : false
          }
        >
          <div>
            <Button
              disabled={
                !activeBtns.requestSignature || hasChanged || !isEditMode
              }
              label="署名依頼"
              onClick={() =>
                openConfirmModal(CONTRACT_STATUS.PENDING_SIGNATURE)
              }
            />
          </div>
        </Tooltip>
      </>
    );
  };

  const onOkModalChangeStatus = async () => {
    try {
      await changeStatusAsync({
        contract_status: targetStatus,
      });
      setOpenModalChangeStatus(false);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    appType,
    openModalChangeStatus,
    setOpenModalChangeStatus,
    modalContent,
    targetStatus,
    permission: {
      create: true,
      view: true,
      edit: true,
      delete: true,
      all: true,
      ...(props.permission || {}),
    },
    extendsButtonRight,
    onOkModalChangeStatus,
    isLoadingChangeStatus,
    disabledInEditMode: currentContractStatus !== CONTRACT_STATUS.DRAFTING,
    ...props,
    configShowBtnsOnHeader: {
      btnPrintPdf: true,
      ...props.configShowBtnsOnHeader,
    },
  };
};

export type Props = ReturnType<typeof useEdit>;

export default useEdit;
